import i18next from "i18next";

import AdminRoleByScope from "./AdminRoleByScope";

import type {
  AdminUserRolesByScope,
  RoleByScope,
} from "../api/AdminUserAPI.types";

class AdminUserRoles {
  t: (key: string) => string;

  lang: string;

  projectList: AdminRoleByScope[];

  channelList: AdminRoleByScope[];

  organizationList: AdminRoleByScope[];

  roleList: string[];

  rolesString: string;

  rolesStringByProject: Record<string, string> | string;

  rolesStringByChannel: Record<string, string> | string;

  rolesStringByOrganization: Record<string, string> | string;

  roleCount: number;

  constructor(
    rolesByScope: AdminUserRolesByScope,
    lang: string = i18next.language,
  ) {
    this.t = i18next.getFixedT(lang, "editor-users");
    this.lang = lang;
    this.projectList = this.initRoleListByScope(
      rolesByScope?.storefront_projects || [],
    );
    this.channelList = this.initRoleListByScope(
      rolesByScope?.fast_channels || [],
    );
    this.organizationList = this.initRoleListByScope(
      rolesByScope?.organization || [],
    );
    this.roleList = this.initRoleList();
    this.rolesString = this.initRolesString();
    this.rolesStringByProject = this.initRolesStringByScope("projectList");
    this.rolesStringByChannel = this.initRolesStringByScope("channelList");
    this.rolesStringByOrganization =
      this.initRolesStringByScope("organizationList");
    this.roleCount = this.initRoleCount();
  }

  translateRoles = (roles: string[]): string => {
    const translatedRoles = roles?.map(this.t);
    return translatedRoles?.join(", ");
  };

  initRoleCount(): number {
    return this.roleList.length;
  }

  initRoleListByScope = (roleListByScope: RoleByScope[]): AdminRoleByScope[] =>
    roleListByScope?.length
      ? roleListByScope.map((scope) => new AdminRoleByScope(scope, this.lang))
      : [];

  initRoleList(): string[] {
    return [
      ...new Set(
        this.projectList
          .flatMap((project) => project.roleList)
          .concat(
            this.channelList.flatMap((channel) => channel.roleList),
            this.organizationList.flatMap(
              (organization) => organization.roleList,
            ),
          ),
      ),
    ];
  }

  get projectIds(): string[] {
    if (this.isAdminOrOwner) {
      return ["all"];
    }
    if (!this.projectList.length) return [];

    return this.projectList.map((project) => project.uuid);
  }

  get organizationId(): string[] {
    if (this.isAdminOrOwner) {
      return ["all"];
    }
    if (!this.organizationList.length) return [];

    return this.organizationList.map((orga) => orga.uuid);
  }

  get channelId(): string[] {
    if (this.isAdminOrOwner) {
      return ["all"];
    }
    if (!this.channelList.length) return [];

    return this.channelList.map((channel) => channel.uuid);
  }

  /**
   * Returns the roles for the user
   * @returns list of roles, comma separated
   */
  initRolesString(): string {
    return this.translateRoles(this.roleList);
  }

  initRolesStringByScope(
    scope: "projectList" | "channelList" | "organizationList",
  ): Record<string, string> | string {
    if (this.isAdminOrOwner) {
      return { all: this.translateRoles(this.roleList) };
    }

    if (!this[scope]?.length) return this.t("noRoles");

    return this[scope]?.reduce(
      (acc: Record<string, string>, currentScope: AdminRoleByScope) => {
        acc[currentScope.uuid] = currentScope.roleList.length
          ? this.translateRoles(currentScope.roleList)
          : this.t("noRoles");

        return acc;
      },
      {},
    );
  }

  get isOwner(): boolean {
    return this.roleList.includes("owner");
  }

  get isAdminOrOwner(): boolean {
    return this.roleList.some((role) =>
      AdminRoleByScope.adminRoles.includes(role),
    );
  }

  get projectById(): Record<string, AdminRoleByScope> | { all: string[] } {
    if (this.isAdminOrOwner) {
      return { all: this.roleList };
    }

    return this.projectList?.reduce(
      (acc: Record<string, AdminRoleByScope>, currentProject) => {
        acc[currentProject.uuid] = currentProject;
        return acc;
      },
      {},
    );
  }

  get channelById(): Record<string, AdminRoleByScope> | { all: string[] } {
    if (this.isAdminOrOwner) {
      return { all: this.roleList };
    }

    return this.channelList?.reduce(
      (acc: Record<string, AdminRoleByScope>, currentChannel) => {
        acc[currentChannel.uuid] = currentChannel;
        return acc;
      },
      {},
    );
  }
}

export default AdminUserRoles;
